import type { ReactNode } from "react";
import { Link } from "react-router";

import type {
  AdminGuide,
  GuideAccordionItem,
  GuideChecklist,
} from "@/guides/adminGuides.types";
import {
  VNextButton,
  VNextCard,
  VNextNotice,
  VNextPill,
} from "@/vnext/primitives";
import {
  ADMIN_GUIDES_ROUTE_BASE,
  getGuideLocationTone,
  getGuideRiskTone,
} from "@/vnext/routes/settings/guides/guidesRouteModel";

interface GuideDetailCardProps {
  invalidGuideId?: string;
  onBrowseAll: () => void;
  onCopyLink: () => Promise<void>;
  selectedGuide?: AdminGuide;
}

const INLINE_GUIDE_TOKEN_PATTERN = /(\*\*[^*]+\*\*|`[^`]+`)/g;

const renderGuideText = (text: string): ReactNode => {
  const tokens = text.split(INLINE_GUIDE_TOKEN_PATTERN).filter(Boolean);

  return tokens.map((token, index) => {
    if (token.startsWith("**") && token.endsWith("**")) {
      return <strong key={`${token}-${index}`}>{token.slice(2, -2)}</strong>;
    }

    if (token.startsWith("`") && token.endsWith("`")) {
      return <code key={`${token}-${index}`}>{token.slice(1, -1)}</code>;
    }

    return token;
  });
};

const renderTextList = (items: string[], ordered = false) => {
  const Component = ordered ? "ol" : "ul";

  return (
    <Component className="guides-route__text-list">
      {items.map((item, index) => (
        <li key={`${index}-${item}`}>{renderGuideText(item)}</li>
      ))}
    </Component>
  );
};

const renderAccordionList = (
  title: string,
  items: GuideAccordionItem[] | undefined,
) => {
  if (!items?.length) {
    return null;
  }

  return (
    <section className="guides-route__detail-section">
      <h3>{title}</h3>
      <div className="guides-route__accordion-list">
        {items.map((item) => (
          <details className="guides-route__accordion" key={item.title}>
            <summary>{item.title}</summary>
            {renderTextList(item.body)}
          </details>
        ))}
      </div>
    </section>
  );
};

const renderChecklistBlocks = (checklists: GuideChecklist[] | undefined) => {
  if (!checklists?.length) {
    return null;
  }

  return (
    <section className="guides-route__detail-section">
      <h3>Checklists</h3>
      <div className="guides-route__checklists">
        {checklists.map((checklist) => (
          <article className="guides-route__checklist" key={checklist.title}>
            <h4>{checklist.title}</h4>
            {renderTextList(checklist.items)}
          </article>
        ))}
      </div>
    </section>
  );
};

const renderDecisionTable = (guide: AdminGuide) => {
  if (!guide.decisionTable?.length) {
    return null;
  }

  return (
    <section className="guides-route__detail-section">
      <h3>Decision guide</h3>
      <div className="guides-route__table-shell">
        <table className="guides-route__table">
          <thead>
            <tr>
              <th>Situation</th>
              <th>Use</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {guide.decisionTable.map((row) => (
              <tr key={row.situation}>
                <td>{row.situation}</td>
                <td>{row.use}</td>
                <td>{row.notes ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export function GuideDetailCard({
  invalidGuideId,
  onBrowseAll,
  onCopyLink,
  selectedGuide,
}: GuideDetailCardProps) {
  if (invalidGuideId) {
    return (
      <VNextCard
        actions={
          <VNextButton onClick={onBrowseAll} tone="secondary">
            Browse all guides
          </VNextButton>
        }
        className="guides-route__detail-card"
        title="Guide not found"
      >
        <VNextNotice tone="warning">
          The guide id <code>{invalidGuideId}</code> does not exist in the
          current registry.
        </VNextNotice>
      </VNextCard>
    );
  }

  if (!selectedGuide) {
    return (
      <VNextCard
        className="guides-route__detail-card"
        subtitle="Choose a guide from the catalog to load its steps, safety notes, and related admin links."
        title="Select a guide"
      >
        <div className="guides-route__empty-detail">
          <p>
            Deep links stay stable at{" "}
            <code>{ADMIN_GUIDES_ROUTE_BASE}/&lt;guideId&gt;</code>.
          </p>
          <p>
            Use this library as the first stop before escalating an internal
            operator workflow.
          </p>
        </div>
      </VNextCard>
    );
  }

  return (
    <VNextCard
      actions={
        <VNextButton onClick={() => void onCopyLink()} tone="secondary">
          Copy guide link
        </VNextButton>
      }
      className="guides-route__detail-card"
    >
      <div className="guides-route__detail-header">
        <div>
          <h2>{selectedGuide.title}</h2>
          <p>{selectedGuide.summary}</p>
        </div>
        <div className="guides-route__guide-pills">
          <VNextPill tone={getGuideRiskTone(selectedGuide.risk)}>
            {selectedGuide.risk.toUpperCase()}
          </VNextPill>
          <VNextPill tone={getGuideLocationTone(selectedGuide.location)}>
            {selectedGuide.location}
          </VNextPill>
          <VNextPill>{selectedGuide.category}</VNextPill>
          {selectedGuide.audience.map((audience) => (
            <VNextPill key={audience}>{audience}</VNextPill>
          ))}
        </div>
      </div>

      {selectedGuide.location === "NotSupportedYet" ? (
        <VNextNotice tone="warning">
          This workflow is documented for support context, but it is not yet
          available as a supported admin flow.
        </VNextNotice>
      ) : null}

      {selectedGuide.warnings?.length ? (
        <VNextNotice
          tone={selectedGuide.risk === "high" ? "danger" : "warning"}
        >
          <div>
            <strong>
              {selectedGuide.risk === "high" ? "Warning" : "Important"}
            </strong>
            {renderTextList(selectedGuide.warnings)}
          </div>
        </VNextNotice>
      ) : null}

      {selectedGuide.safetyNotes?.length ? (
        <VNextNotice tone="info">
          <div>
            <strong>Safety / privacy</strong>
            {renderTextList(selectedGuide.safetyNotes)}
          </div>
        </VNextNotice>
      ) : null}

      <section className="guides-route__detail-section">
        <h3>What this helps you do</h3>
        {renderTextList(selectedGuide.whatThisHelpsYouDo)}
      </section>

      <section className="guides-route__detail-section">
        <h3>When to use this</h3>
        {renderTextList(selectedGuide.whenToUse)}
      </section>

      <section className="guides-route__detail-section">
        <h3>When not to use this</h3>
        {renderTextList(selectedGuide.whenNotToUse)}
      </section>

      <section className="guides-route__detail-section">
        <h3>Before you start</h3>
        {renderTextList(selectedGuide.beforeYouStart)}
      </section>

      {renderDecisionTable(selectedGuide)}

      <section className="guides-route__detail-section">
        <h3>Steps</h3>
        {renderTextList(selectedGuide.steps, true)}
      </section>

      {renderChecklistBlocks(selectedGuide.checklists)}

      <section className="guides-route__detail-section">
        <h3>What success looks like</h3>
        {renderTextList(selectedGuide.whatSuccessLooksLike)}
      </section>

      {renderAccordionList(
        "Common problems (and fixes)",
        selectedGuide.troubleshooting,
      )}
      {renderAccordionList("FAQs", selectedGuide.faqs)}

      <VNextNotice tone={selectedGuide.risk === "high" ? "warning" : "info"}>
        <div>
          <strong>Escalation: when to ask Engineering</strong>
          {renderTextList(selectedGuide.escalateWhen)}
        </div>
      </VNextNotice>

      {(selectedGuide.relatedGuides?.length ?? 0) > 0 ||
      (selectedGuide.relatedAdminPaths?.length ?? 0) > 0 ? (
        <section className="guides-route__detail-section">
          <h3>Related guides</h3>
          {selectedGuide.relatedGuides?.length ? (
            <ul className="guides-route__link-list">
              {selectedGuide.relatedGuides.map((item) => (
                <li key={item.guideId}>
                  <Link to={`${ADMIN_GUIDES_ROUTE_BASE}/${item.guideId}`}>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
          {selectedGuide.relatedAdminPaths?.length ? (
            <>
              <h4>Related admin pages</h4>
              <ul className="guides-route__link-list">
                {selectedGuide.relatedAdminPaths.map((item) => (
                  <li key={item.path}>
                    <Link to={item.path}>{item.label}</Link>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </section>
      ) : null}

      <section className="guides-route__detail-section">
        <h3>Sources (internal)</h3>
        <ul className="guides-route__link-list">
          {selectedGuide.sourceDocs.map((docPath) => (
            <li key={docPath}>
              <code>{docPath}</code>
            </li>
          ))}
        </ul>
      </section>
    </VNextCard>
  );
}
